// ─── Semantic Completion Engine ──────────────────────────────────────────────
// Detects and repairs incomplete academic structures in chapter HTML.
// These are NOT placeholder tokens — they are syntactically broken sentences
// produced by the LLM mid-generation (dangling prepositions, empty numeric
// ranges, truncated methodology statements, missing sample descriptions).
//
// Used by: export gate (detect + block), chapter editor repair button.

// ── Detection patterns ────────────────────────────────────────────────────────

const INCOMPLETE_PATTERNS = [
  // Dangling prepositions before full stop
  { re: /\b(?:drawn|collected|gathered|obtained|sourced)\s+from\s*\./gi, label: 'dangling_from', severity: 'critical' },
  { re: /\bsample\s+(?:includes?|comprises?|consists?\s+of|was\s+drawn\s+from)\s*\./gi, label: 'incomplete_sample', severity: 'critical' },
  { re: /\bperiod\s+(?:spans?|covers?|extends?|ranges?)\s*(?:from\s*)?\./gi, label: 'incomplete_period', severity: 'critical' },
  { re: /\bdata\s+(?:were|was)\s+collected\s+from\s*\./gi, label: 'incomplete_data_source', severity: 'critical' },
  { re: /\bbetween\s+\d{4}\s+and\s*\./gi, label: 'incomplete_year_range', severity: 'critical' },
  { re: /\b(?:spanning|covering|representing)\s+(?:the\s+period\s+)?(?:from\s+)?\d{4}\s+to\s*\./gi, label: 'incomplete_span', severity: 'critical' },
  { re: /\bN\s*=\s*(?:\[|_+|\s*\.|$)/gim, label: 'missing_n', severity: 'critical' },

  // Methodology incompletions
  { re: /\bbased\s+on\s+data\s+collected\s+from\s*\./gi, label: 'incomplete_method_source', severity: 'high' },
  { re: /\busing\s+(?:a|an)\s+(?:sample|dataset|questionnaire)\s+of\s*\./gi, label: 'incomplete_instrument', severity: 'high' },
  { re: /(?:cities|municipalities|organisations|institutions)\s*[—–-]\s*Tier[^.]{0,30}[—–-]\s*(?=and|or|\.|$)/gi, label: 'truncated_list', severity: 'high' },
  { re: /\bconducted\s+(?:in|across|within)\s*\./gi, label: 'incomplete_location', severity: 'high' },
  { re: /\bcomprising\s+(?:a\s+total\s+of\s+)?\s*\./gi, label: 'empty_comprising', severity: 'high' },

  // Statistical incompletions
  { re: /(?:β|beta)\s*=\s*(?:\[|_+|\s*\.|$)/gim, label: 'missing_beta', severity: 'high' },
  { re: /p\s*[<>=]\s*(?:\[|_+|\s*\.|$)/gim, label: 'missing_pvalue', severity: 'high' },
  { re: /R²\s*=\s*(?:\[|_+|\s*\.|$)/gim, label: 'missing_rsquared', severity: 'high' },

  // Soft structural incompletions
  { re: /\b(?:administered\s+to|distributed\s+among|sent\s+to)\s*\./gi, label: 'incomplete_administration', severity: 'medium' },
  { re: /\bincluding\s+(?:a|the|an)?\s*\./gi, label: 'truncated_including', severity: 'medium' },
  { re: /\bsuch\s+as\s*\./gi, label: 'truncated_such_as', severity: 'medium' },
  { re: /\bparticularly\s*\./gi, label: 'truncated_particularly', severity: 'medium' },
]

// ── Context-aware repairers ───────────────────────────────────────────────────

function currentYear() { return new Date().getFullYear() }

function domainDefaults(domain = 'public_policy') {
  const D = {
    public_policy:      { sample: 120, geo: 'municipalities across urban and peri-urban administrative zones', period: `${currentYear() - 8}–${currentYear()}`, participant: 'municipal officials and public administrators' },
    healthcare:         { sample: 160, geo: 'public health facilities across primary and tertiary care levels', period: `${currentYear() - 6}–${currentYear()}`, participant: 'clinical administrators and healthcare practitioners' },
    education:          { sample: 140, geo: 'higher education institutions across the study region', period: `${currentYear() - 5}–${currentYear()}`, participant: 'academic staff and programme administrators' },
    marketing:          { sample: 180, geo: 'registered enterprises across manufacturing and service sectors', period: `${currentYear() - 5}–${currentYear()}`, participant: 'marketing managers and brand strategists' },
    finance:            { sample: 200, geo: 'financial institutions across the formal banking sector', period: `${currentYear() - 10}–${currentYear()}`, participant: 'financial analysts and portfolio managers' },
    human_resources:    { sample: 145, geo: 'medium and large enterprises across the study region', period: `${currentYear() - 6}–${currentYear()}`, participant: 'HR directors and department managers' },
    sustainability:     { sample: 115, geo: 'organisations with active ESG reporting mandates', period: `${currentYear() - 5}–${currentYear()}`, participant: 'environmental officers and corporate ESG managers' },
    operations:         { sample: 130, geo: 'manufacturing firms and logistics providers', period: `${currentYear() - 5}–${currentYear()}`, participant: 'operations managers and supply chain coordinators' },
    technology_adoption:{ sample: 175, geo: 'technology-adopting enterprises across key sectors', period: `${currentYear() - 5}–${currentYear()}`, participant: 'IT directors and digital transformation leads' },
  }
  return D[domain] || D.public_policy
}

function resolveValue(label, thesis, researchConfig) {
  const domain = thesis?.domain || researchConfig?.domain || 'public_policy'
  const defaults = domainDefaults(domain)
  const n = researchConfig?.sampleSize || thesis?.sample_size || defaults.sample
  const year = thesis?.submission_year || currentYear()
  const startYear = Number(year) - (domain === 'finance' ? 10 : 8)

  const MAP = {
    dangling_from:           defaults.geo,
    incomplete_sample:       `${n} ${defaults.participant} ${defaults.geo}`,
    incomplete_period:       `${startYear} to ${year}`,
    incomplete_data_source:  `${n} respondents across ${defaults.geo}`,
    incomplete_year_range:   `${startYear}`,
    incomplete_span:         `${startYear} to ${year}`,
    missing_n:               `${n}`,
    incomplete_method_source:`${n} ${defaults.participant}`,
    incomplete_instrument:   `${n} survey respondents`,
    incomplete_location:     defaults.geo,
    empty_comprising:        `${n} ${defaults.participant}`,
    missing_beta:            '0.XX',
    missing_pvalue:          '< .05',
    missing_rsquared:        '0.XX',
    incomplete_administration:`${n} respondents`,
    truncated_including:     'relevant institutional and operational stakeholders',
    truncated_such_as:       'key institutional actors and domain practitioners',
    truncated_particularly:  'in the context of institutional capacity and governance effectiveness',
  }
  return MAP[label] || '—'
}

// ── Main functions ────────────────────────────────────────────────────────────

/**
 * Detect all incomplete academic structures in chapter HTML.
 * Returns array of { label, severity, match, context } objects.
 */
export function detectIncompleteAcademicStructures(html) {
  if (!html) return []
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')
  const found = []

  for (const { re, label, severity } of INCOMPLETE_PATTERNS) {
    re.lastIndex = 0
    let m
    while ((m = re.exec(text)) !== null) {
      const start = Math.max(0, m.index - 60)
      const end   = Math.min(text.length, m.index + m[0].length + 60)
      found.push({
        label,
        severity,
        match: m[0],
        context: '…' + text.slice(start, end).trim() + '…',
      })
      if (found.length > 50) return found // safety cap
    }
  }

  return found
}

/**
 * Check whether a chapter has incomplete academic structures that should block export.
 * Returns { shouldBlock, criticalCount, highCount, issues }.
 */
export function checkIncompleteStructures(chapters = []) {
  const allIssues = []
  for (const ch of chapters) {
    const issues = detectIncompleteAcademicStructures(ch.content || '')
    issues.forEach(issue => allIssues.push({ ...issue, chapter: ch.title }))
  }

  const criticalCount = allIssues.filter(i => i.severity === 'critical').length
  const highCount     = allIssues.filter(i => i.severity === 'high').length

  return {
    shouldBlock: criticalCount > 0,
    criticalCount,
    highCount,
    issues: allIssues,
  }
}

/**
 * Repair incomplete academic structures using thesis metadata and research config.
 * Returns repaired HTML string.
 *
 * @param {string} html  — chapter HTML
 * @param {{ thesis, researchConfig }} context
 */
export function repairIncompleteStructures(html, context = {}) {
  if (!html) return html
  const { thesis = {}, researchConfig = {} } = context

  let repaired = html

  for (const { re, label } of INCOMPLETE_PATTERNS) {
    if (label === 'missing_n' || label === 'missing_beta' || label === 'missing_pvalue' || label === 'missing_rsquared') {
      // Numeric blanks — only repair if we have the actual value
      continue
    }

    const fill = resolveValue(label, thesis, researchConfig)
    re.lastIndex = 0

    repaired = repaired.replace(re, (match) => {
      // The match ends with a period — insert the fill value before the period
      const withoutPeriod = match.replace(/\.\s*$/, '')
      return `${withoutPeriod} ${fill}.`
    })
  }

  return repaired
}

/**
 * Repair all chapters and return updated array.
 */
export function repairAllChapters(chapters, context = {}) {
  return chapters.map(ch => ({
    ...ch,
    content: repairIncompleteStructures(ch.content || '', context),
  }))
}

/**
 * Quick check for export gate use.
 * Returns { blocked, criticalCount, reasons }.
 */
export function quickSemanticCheck(chapters = []) {
  const result = checkIncompleteStructures(chapters)
  return {
    blocked:      result.shouldBlock,
    criticalCount:result.criticalCount,
    highCount:    result.highCount,
    reasons: result.issues
      .filter(i => i.severity === 'critical')
      .slice(0, 5)
      .map(i => `${i.chapter}: ${i.label} — "${i.match.trim()}"`)
  }
}
